import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/useToast";
import {
  editMessageInChat,
  postNewMessageInChat,
  replyToMessageInChat,
  postNewMessageWithMediaAttachmentToChat,
  postNewMessageWithFileAttachmentToChat,
} from "@/middleware/messages";
import { computeMessageLimitState } from "@/lib/messageLimits";
import { toPlainText } from "@/lib/markdown";
import { isVideoType } from "@/utils/attachmentType";
import { useMediaMessageSenderState } from "@/zustand/MediaMessageSenderState";
import { chunkedSessionCount } from "@/zustand/UploadControlState";
import { heldUploadSlotCount } from "@/lib/uploadSlots";
import {
  LARGE_ATTACHMENT_THRESHOLD,
  MAX_PARALLEL_CHUNKED_UPLOADS,
} from "@/config/constants";
import { useFileMessageSenderState } from "@/zustand/FileMessageSenderState";
import { useNavigationState } from "@/zustand/NavigationState";
import { useRtfInputForChat } from "@/hooks/useRtfInputForChat";
import { useRtfInputState } from "@/zustand/RtfInputState";
import type { RtfLinkType } from "@/middleware/links";

interface UseRtfHandlersOptions {
  value: string;
  clearContent: () => void;
  focus: () => void;
  /** Emit typing=false. Called only once a send is ACCEPTED (past validation). */
  stopTyping?: () => void;
}

export function useRtfHandlers({
  value,
  clearContent,
  focus,
  stopTyping,
}: UseRtfHandlersOptions) {
  const activeChatId = useNavigationState((state) => state.activeChatId);
  const { t } = useTranslation();
  const {
    editMessage,
    replyMessage,
    clearDraftMessage,
    clearEditMessage,
    clearReplyMessage,
    clearLinkMessage,
    clearLinkMessageMetadata,
  } = useRtfInputForChat(activeChatId);
  const { toast } = useToast();

  const {
    newMediaAttachment,
    newMediaAttachmentId,
    newMediaThumbnailId,
    thumbnailBlob,
    discardMediaAttachment,
  } = useMediaMessageSenderState();

  const { newFileAttachment, newFileAttachmentId, discardFileAttachment } =
    useFileMessageSenderState();

  /**
   * Extracts and validates content from the textarea.
   * Sends raw markdown — no HTML conversion needed.
   */
  const extractEditorContent = () => {
    const plainText = toPlainText(value);

    if (!plainText.trim().length) {
      return {
        content: "",
        hasContent: false,
        limitState: computeMessageLimitState({ plainText: "", html: "" }),
      };
    }

    // plainText: stripped of markdown syntax (for user-facing char count)
    // html: original markdown value (for encrypted size prediction)
    const limitState = computeMessageLimitState({
      plainText,
      html: value,
    });

    return {
      content: value,
      hasContent: true,
      limitState,
    };
  };

  const hasValidMediaAttachment = () => {
    // Deliberately does NOT require thumbnailBlob: the thumbnail is captured
    // in the BACKGROUND after staging (stage-first). Requiring it here would
    // make a send during capture silently go out as text-only, stranding the
    // staged video. The send paths await the capture via
    // resolveStagedThumbnail() instead (bounded; placeholder-backed).
    return !!(
      newMediaAttachment &&
      newMediaAttachmentId &&
      newMediaThumbnailId
    );
  };

  // Resolves the thumbnail for a media send. CLOSURE-FIRST: handleSendMessage
  // clears the staging state BEFORE the send functions run (optimistic input
  // clear), so current state is empty by the time this executes — the values
  // this hook destructured (thumbnailBlob, ids) are what survive, exactly
  // like `newMediaAttachment!` in the callers. When the background capture
  // hasn't settled yet (video staged and sent quickly), fall back to the
  // capture map keyed by the CLOSURE id. Null only when even the placeholder
  // failed (broken canvas): destructive toast, abort the send.
  const resolveStagedThumbnail = async (
    attachmentId: string,
  ): Promise<Blob | null> => {
    const thumb =
      thumbnailBlob ??
      (await useMediaMessageSenderState
        .getState()
        .waitForStagedThumbnail(attachmentId));
    if (!thumb) {
      toast({
        duration: 1500,
        description: t("errorCreatingVideoThumbnail"),
        variant: "destructive",
      });
    }
    return thumb;
  };

  const hasValidFileAttachment = () => {
    return !!(newFileAttachment && newFileAttachmentId);
  };

  const handleEditMessage = async (chatId: string, content: string) => {
    if (!editMessage) {
      return;
    }

    const hasMediaAttachment = hasValidMediaAttachment();
    const hasFileAttachment = hasValidFileAttachment();

    const hasExistingAttachment = editMessage.hasAttachment;
    const isRemovingAttachment =
      hasExistingAttachment && !hasMediaAttachment && !hasFileAttachment;

    const existingMediaThumbnailId =
      editMessage.content?.media?.attachmentThumbnailId;
    const isSameMediaAttachment =
      hasMediaAttachment &&
      existingMediaThumbnailId &&
      newMediaThumbnailId === existingMediaThumbnailId;

    const existingFileAttachmentId = editMessage.content?.file?.attachmentId;
    const isSameFileAttachment =
      hasFileAttachment &&
      existingFileAttachmentId &&
      newFileAttachmentId === existingFileAttachmentId;
    const isKeepingExistingAttachment =
      hasExistingAttachment &&
      !isRemovingAttachment &&
      (isSameMediaAttachment ||
        isSameFileAttachment ||
        (!hasMediaAttachment && !hasFileAttachment));

    if (isRemovingAttachment) {
      await editMessageInChat({
        chatId,
        message: editMessage,
        newContent: content,
        removeAttachment: true,
      });
    } else if (isKeepingExistingAttachment) {
      await editMessageInChat({
        chatId,
        message: editMessage,
        newContent: content,
        keepExistingAttachment: true,
      });
    } else if (hasMediaAttachment) {
      const resolvedThumb = await resolveStagedThumbnail(newMediaAttachmentId!);
      if (!resolvedThumb) {
        return;
      }
      await editMessageInChat({
        chatId,
        message: editMessage,
        newContent: content,
        newMediaAttachment: {
          file: newMediaAttachment!,
          attachmentId: newMediaAttachmentId!,
          thumbnailId: newMediaThumbnailId!,
          thumbnailBlob: resolvedThumb,
        },
      });
    } else if (hasFileAttachment) {
      await editMessageInChat({
        chatId,
        message: editMessage,
        newContent: content,
        newFileAttachment: {
          file: newFileAttachment!,
          attachmentId: newFileAttachmentId!,
        },
      });
    } else {
      await editMessageInChat({
        chatId,
        message: editMessage,
        newContent: content,
      });
    }
  };

  const handleReplyWithMediaAttachment = async (
    chatId: string,
    content: string,
  ) => {
    if (!replyMessage) {
      return;
    }
    const resolvedThumb = await resolveStagedThumbnail(newMediaAttachmentId!);
    if (!resolvedThumb) {
      return;
    }
    await postNewMessageWithMediaAttachmentToChat({
      chatId,
      file: newMediaAttachment!,
      newAttachmentId: newMediaAttachmentId!,
      newThumbnailId: newMediaThumbnailId!,
      thumbnailBlob: resolvedThumb,
      content,
      parentMessage: replyMessage,
    });
  };

  const handleReplyWithFileAttachment = async (
    chatId: string,
    content: string,
  ) => {
    if (!replyMessage) {
      return;
    }
    await postNewMessageWithFileAttachmentToChat({
      chatId,
      file: newFileAttachment!,
      newAttachmentId: newFileAttachmentId!,
      content,
      parentMessage: replyMessage,
    });
  };

  const handleMediaAttachmentMessage = async (
    chatId: string,
    content: string,
  ) => {
    const resolvedThumb = await resolveStagedThumbnail(newMediaAttachmentId!);
    if (!resolvedThumb) {
      return;
    }
    await postNewMessageWithMediaAttachmentToChat({
      chatId,
      file: newMediaAttachment!,
      newAttachmentId: newMediaAttachmentId!,
      newThumbnailId: newMediaThumbnailId!,
      thumbnailBlob: resolvedThumb,
      content,
    });
  };

  const handleFileAttachmentMessage = async (
    chatId: string,
    content: string,
  ) => {
    await postNewMessageWithFileAttachmentToChat({
      chatId,
      file: newFileAttachment!,
      newAttachmentId: newFileAttachmentId!,
      content,
    });
  };

  const handleReplyMessage = async (chatId: string, content: string) => {
    if (!replyMessage) {
      return;
    }
    await replyToMessageInChat({
      chatId,
      parentMessage: replyMessage,
      content,
    });
  };

  const handleRegularMessage = async (
    chatId: string,
    content: string,
    linkMeta?: RtfLinkType,
  ) => {
    await postNewMessageInChat({
      chatId,
      content,
      linkMetadata: linkMeta,
    });
  };

  const handleSendMessage = async () => {
    if (!activeChatId) {
      return;
    }

    const { content, hasContent, limitState } = extractEditorContent();

    if (limitState.isOverCharLimit || limitState.isOverEncryptedLimit) {
      toast({
        duration: 1500,
        description: t("editor.messageTooLarge"),
        variant: "destructive",
      });
      return;
    }

    const hasMediaAttachment = hasValidMediaAttachment();
    const hasFileAttachment = hasValidFileAttachment();

    if (
      !hasContent &&
      !hasMediaAttachment &&
      !hasFileAttachment &&
      !editMessage
    ) {
      return;
    }

    // Parallel-session cap (v1, client gate): at most
    // MAX_PARALLEL_CHUNKED_UPLOADS chunked uploads at once — bandwidth stays
    // focused on the running transfers (watch-while-uploading receivers chase
    // the slowest upload), memory stays bounded, and it's the cheapest abuse
    // brake. Checked BEFORE the optimistic clear below so a blocked send
    // leaves the staged video in the input untouched.
    //
    // VIDEO only: the chunked path is video-only, so this cap must not fire for
    // other kinds. It used to skip the type check on the premise "images can't
    // exceed 40 MB" (< LARGE_ATTACHMENT_THRESHOLD) — no longer true, since paid
    // regular-chat images now reach 100 MB. Without the isVideoType guard a
    // large image would be wrongly blocked with the "chunked upload limit"
    // toast even though it never uses the chunked path.
    if (
      hasMediaAttachment &&
      newMediaAttachment &&
      isVideoType(newMediaAttachment.type) &&
      newMediaAttachment.size > LARGE_ATTACHMENT_THRESHOLD
    ) {
      // Active session count: cross-tab via Web Locks where supported (all
      // tabs of this origin), per-tab registry fallback elsewhere. One cap
      // comparison over whichever count we got.
      const activeSessions =
        (await heldUploadSlotCount()) ?? chunkedSessionCount();
      if (activeSessions >= MAX_PARALLEL_CHUNKED_UPLOADS) {
        toast({
          duration: 2500,
          description: t("chunkedUploadLimitReached", {
            count: MAX_PARALLEL_CHUNKED_UPLOADS,
          }),
        });
        return;
      }
    }

    // Send is now ACCEPTED (past every validation early-return). Stop the
    // typing indicator immediately — a failed-validation return above must NOT
    // reach here, so a rejected send leaves the indicator on (still typing).
    stopTyping?.();

    // Snapshot link metadata before clearing state — postNewMessageInChat
    // needs it for the optimistic message, but we clear Zustand below for
    // UI responsiveness.
    const linkMetadata =
      useRtfInputState.getState().linkMessagesMetadata[activeChatId];

    // Clear the input immediately so the UI feels responsive.
    clearContent();
    // Defer focus so the browser processes the content clear first.
    // On mobile this keeps the soft keyboard visible and ensures the
    // next message starts with autocapitalisation (as if at a sentence start).
    setTimeout(() => focus(), 0);
    discardFileAttachment();
    discardMediaAttachment();
    clearDraftMessage();
    clearEditMessage();
    clearReplyMessage();
    clearLinkMessage();
    clearLinkMessageMetadata();

    try {
      switch (true) {
        case !!editMessage:
          await handleEditMessage(activeChatId, content);
          break;
        case !!(replyMessage && hasMediaAttachment):
          await handleReplyWithMediaAttachment(activeChatId, content);
          break;
        case !!(replyMessage && hasFileAttachment):
          await handleReplyWithFileAttachment(activeChatId, content);
          break;
        case !!hasMediaAttachment:
          await handleMediaAttachmentMessage(activeChatId, content);
          break;
        case !!hasFileAttachment:
          await handleFileAttachmentMessage(activeChatId, content);
          break;
        case !!replyMessage:
          await handleReplyMessage(activeChatId, content);
          break;
        default:
          await handleRegularMessage(activeChatId, content, linkMetadata);
          break;
      }
    } catch (error) {
      toast({
        duration: 1000,
        description:
          error instanceof Error ? error.message : "Cannot send message",
        variant: "destructive",
      });
    }
  };

  return {
    handleSendMessage,
  };
}
